import { Evidence } from "@p0security/shared/types/evidence";
import { PermissionRequest } from "@p0security/shared/types/permission";
import {
  directories,
  resourceIntegrations,
} from "components/Integrations/constants";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useMemo } from "react";

import {
  requestDescription,
  requestLocator,
  toRequestDescription,
  toRequestLocation,
} from "../requestUtils";
import { FilterOption } from "../types.ts";
import { useFilterOptions } from "./useFilterOptions";

export type FilterOptionsResult = {
  requestorOptions: FilterOption[];
  approverOptions: FilterOption[];
  resourceOptions: FilterOption[];
  parentOptions: FilterOption[];
};

/**
 * Internal helper to build filter options using a config.
 * Accepts integrations and flags as parameters to avoid redundant context calls.
 */
const useFilterOptionsWithConfig = <T>(
  items: T[],
  config: {
    getRequestor: (item: T) => string;
    getApprover: (item: T) => string | null;
    getResource: (item: T) => string;
    getParent: (item: T, flags: Record<string, boolean>) => string | null;
  },
  flags: Record<string, boolean>
): FilterOptionsResult => {
  return {
    requestorOptions: useFilterOptions(items, config.getRequestor),
    approverOptions: useFilterOptions(items, config.getApprover),
    resourceOptions: useFilterOptions(items, config.getResource),
    parentOptions: useFilterOptions(items, (item) =>
      config.getParent(item, flags)
    ),
  };
};

/**
 * Named helper for pre-approval parent location resolution.
 * Clearer than inline ternary + nullish coalescing.
 */
const getPreApprovalLocation = (
  evidence: Evidence,
  integrationMap: Array<{ key: string; label: string }>
): string | null => {
  // First, try to get location from permission account
  const accountLocation = requestLocator(evidence);
  if (accountLocation) return String(accountLocation);

  // Fallback: find integration by evidence type
  // permissionAccount returns undefined for some types,
  //  so we need to fallback to the integration name itself
  const integration = integrationMap.find((i) => i.key === evidence.type);
  return integration?.label ?? null;
};

/**
 * Extracts filter options for Evidence (pre-approvals) from a list of items.
 *
 * This hook processes Evidence items and extracts unique values for requestor,
 * approver, resource, and location filters.
 *
 * @param items - Array of items containing Evidence data to extract filter options from
 * @returns FilterOptionsResult containing arrays of unique filter values for requestors,
 *          approvers, resources, and locations
 */
export const usePreApprovalFilterOptions = <T extends { data: Evidence }>(
  items: T[]
): FilterOptionsResult => {
  const flags = useFlags();

  const integrationMap = useMemo(
    () => [...resourceIntegrations(flags), ...directories(flags)],
    [flags]
  );

  const config = useMemo(
    () => ({
      getRequestor: (e: T) => e.data.principal,
      getApprover: (e: T) => e.data.approver?.email ?? null,
      getResource: (e: T) => requestDescription(e.data),
      getParent: (e: T) => getPreApprovalLocation(e.data, integrationMap),
    }),
    [integrationMap]
  );

  return useFilterOptionsWithConfig(items, config, flags);
};

/**
 * Extracts filter options for PermissionRequest items from a list of requests.
 *
 * This hook processes PermissionRequest items and extracts unique values for requestor,
 * approver, resource, and location filters.
 *
 * @param items - Array of items containing PermissionRequest data to extract filter options from
 * @returns FilterOptionsResult containing arrays of unique filter values for requestors,
 *          approvers, resources, and locations
 */
export const usePermissionRequestFilterOptions = <
  T extends { data: PermissionRequest },
>(
  items: T[]
): FilterOptionsResult => {
  const flags = useFlags();

  const config = useMemo(
    () => ({
      getRequestor: (r: T) => r.data.requestor,
      getApprover: (r: T) => {
        if (!r.data.approvalDetails) return null;
        const { email, name, id, approvalSource } = r.data.approvalDetails;

        // Handle special approval sources
        if (approvalSource === "evidence") return "Pre Approval";
        if (approvalSource === "persistent") return "By persistent access";

        // Use first available identifier
        return email || name || id || null;
      },
      getResource: (r: T) => toRequestDescription(r.data),
      getParent: (r: T) => toRequestLocation(r.data, flags) ?? null,
    }),
    [flags]
  );

  return useFilterOptionsWithConfig(items, config, flags);
};
